import {
  FloatingArrow,
  Placement,
  arrow,
  autoUpdate,
  flip,
  offset,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { ReactNode, useRef, useState } from "react";

import { TooltipContent } from "./elements";

export type TooltipProps = {
  id?: string;
  children: ReactNode;
  className?: string;
  tooltipText?: string | ReactNode;
  placement?: Placement;
  offsetBy?: number;
};

export const Tooltip = ({
  id,
  className,
  children,
  tooltipText,
  placement = "right",
  offsetBy = 0,
}: TooltipProps) => {
  const tooltipArrowRef = useRef(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltip = useFloating({
    placement,
    open: tooltipOpen,
    onOpenChange: setTooltipOpen,
    middleware: [flip(), arrow({ element: tooltipArrowRef }), offset(offsetBy)],
    whileElementsMounted: autoUpdate,
  });
  const tooltipInteractions = useInteractions([
    useHover(tooltip.context, { move: false }),
    useFocus(tooltip.context),
    useDismiss(tooltip.context),
    useRole(tooltip.context, { role: "tooltip" }),
  ]);

  return (
    <>
      <div
        id={id}
        className={className}
        ref={tooltip.refs.setReference}
        {...tooltipInteractions.getReferenceProps()}
      >
        {children}
      </div>
      {tooltipOpen && !!tooltipText && (
        <TooltipContent
          ref={tooltip.refs.setFloating}
          style={tooltip.floatingStyles}
          {...tooltipInteractions.getFloatingProps()}
        >
          <FloatingArrow
            ref={tooltipArrowRef}
            context={tooltip.context}
            tipRadius={2}
          />
          <p>{tooltipText}</p>
        </TooltipContent>
      )}
    </>
  );
};

export { TooltipContent };
